import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash.debounce';
import DataTable from 'react-data-table-component';
import {
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import {
  getReferralCodes,
  createReferralCode,
  deleteReferralCodes,
} from '../ReduxToolkit/Reducers/organizationManagement';
import { toast } from 'react-toastify';

function ReferralCodeManagementTab() {
  const dispatch = useDispatch();
  const { referralCodes, totalReferralCodes, isLoading } = useSelector(
    (state) => state.organizationManagement,
  );

  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchText, setSearchText] = useState('');
  const [tableData, setTableData] = useState([]);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);

  const [form, setForm] = useState({
    referral_name: '',
    referral_code: '',
    trial_period: '',
    trial_credits: '',
  });

  useEffect(() => {
    if (page) {
      fetchReferralCodes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, itemsPerPage, searchText]);

  useEffect(() => {
    if (referralCodes[page]) {
      setTableData(referralCodes[page]);
    }
  }, [referralCodes, page]);

  const fetchReferralCodes = async (hideLoader = false) => {
    await dispatch(getReferralCodes(page, searchText, itemsPerPage, hideLoader));
  };

  const searchCodes = (inputValue) => {
    const query = inputValue?.trim();
    setPage(1);
    setSearchText(query);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearch = useCallback(debounce(searchCodes, 700), []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    debounceSearch(e.target.value);
  };

  const generateRandomCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let code = '';
    for (let i = 0; i < 36; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setForm((prev) => ({ ...prev, referral_code: code }));
  };

  const onCreateReferralCode = async () => {
    if (!form.referral_name.trim() || !form.referral_code.trim() || form.trial_period === '' || form.trial_credits === '') {
      toast.error('Please fill in all fields.');
      return;
    }

    if (isNaN(Number(form.trial_period)) || isNaN(Number(form.trial_credits))) {
      toast.error('Trial period and trial credits must be valid numbers.');
      return;
    }

    const payload = {
      referral_name: form.referral_name.trim(),
      referral_code: form.referral_code.trim(),
      trial_period: Number(form.trial_period),
      trial_credits: Number(form.trial_credits),
    };

    const result = await dispatch(createReferralCode(payload));
    if (result?.statusCode === 201 || result?.statusCode === 200) {
      setCreateModalOpen(false);
      setForm({
        referral_name: '',
        referral_code: '',
        trial_period: '',
        trial_credits: '',
      });
      fetchReferralCodes();
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) return;
    const confirm = window.confirm(`Are you sure you want to delete ${selectedRows.length} referral code(s)?`);
    if (!confirm) return;

    const ids = selectedRows.map((row) => row.id);
    const result = await dispatch(deleteReferralCodes(ids));
    
    if (result?.statusCode === 200) {
      setClearSelectedRows(!clearSelectedRows);
      setSelectedRows([]);
      fetchReferralCodes();
    }
  };

  const columns = [
    {
      name: 'Referral Name',
      selector: (row) => row.referral_name,
      sortable: true,
      minWidth: '200px',
    },
    {
      name: 'Referral Code',
      selector: (row) => row.referral_code,
      sortable: true,
      minWidth: '150px',
    },
    {
      name: 'Trial Period (Days)',
      selector: (row) => row.trial_period,
      sortable: true,
      minWidth: '150px',
    },
    {
      name: 'Trial Credits',
      selector: (row) => row.trial_credits,
      sortable: true,
      minWidth: '150px',
    },
    {
      name: 'Created At',
      selector: (row) => new Date(row.created_at).toLocaleDateString(),
      sortable: true,
      minWidth: '150px',
    },
    {
      name: 'Copy Signup Link',
      cell: (row) => (
        <Button
          color="info"
          size="sm"
          outline
          onClick={() => {
            const link = `https://app.hyperscout.io/auth/register?ref=${row.referral_code}`;
            navigator.clipboard.writeText(link);
            toast.success('Link copied to clipboard!', { autoClose: 3000 });
          }}
        >
          Copy
        </Button>
      ),
      minWidth: '150px',
    },
  ];

  return (
    <>
      <Card>
        <CardBody>
          <Row className="align-items-center justify-content-between mb-3">
            <Col xs="auto" className="d-flex gap-2">
              <Button color="primary" onClick={() => setCreateModalOpen(true)}>
                Create Referral Code
              </Button>
              {selectedRows.length > 0 && (
                <Button color="danger" onClick={handleDeleteSelected}>
                  Delete Selected
                </Button>
              )}
            </Col>
            <Col xs="auto" className="d-flex align-items-center gap-2">
              <Label className="mb-0">Search:</Label>
              <Input
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search by name or code"
                style={{ minWidth: 320 }}
              />
            </Col>
          </Row>

          <DataTable
            striped
            pagination
            paginationServer
            selectableRows
            onSelectedRowsChange={({ selectedRows }) => setSelectedRows(selectedRows)}
            clearSelectedRows={clearSelectedRows}
            data={tableData}
            columns={columns}
            progressPending={isLoading}
            paginationTotalRows={totalReferralCodes}
            paginationPerPage={itemsPerPage}
            paginationDefaultPage={page}
            customStyles={{ tableWrapper: { style: { minHeight: 500 } } }}
            onChangePage={(newPage) => setPage(newPage)}
            onChangeRowsPerPage={(count) => {
              setPage(1);
              setItemsPerPage(count);
            }}
          />
        </CardBody>
      </Card>

      <Modal isOpen={createModalOpen} toggle={() => setCreateModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setCreateModalOpen(false)}>
          Create Referral Code
        </ModalHeader>
        <ModalBody>
          <Row className="g-3">
            <Col md={6}>
              <FormGroup>
                <Label>Referral Name</Label>
                <Input
                  value={form.referral_name}
                  onChange={(e) => setForm((prev) => ({ ...prev, referral_name: e.target.value }))}
                  placeholder="e.g. Summer Campaign"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Referral Code</Label>
                <div className="d-flex gap-2">
                  <Input
                    value={form.referral_code}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[\s/]/g, '');
                      setForm((prev) => ({ ...prev, referral_code: val }));
                    }}
                    placeholder="e.g. SUMMER2026"
                  />
                  <Button color="secondary" outline onClick={generateRandomCode}>
                    Generate
                  </Button>
                </div>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Trial Period (Days)</Label>
                <Input
                  type="text"
                  value={form.trial_period}
                  onChange={(e) => setForm((prev) => ({ ...prev, trial_period: e.target.value }))}
                  placeholder="14"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Trial Credits</Label>
                <Input
                  type="text"
                  value={form.trial_credits}
                  onChange={(e) => setForm((prev) => ({ ...prev, trial_credits: e.target.value }))}
                  placeholder="50"
                />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setCreateModalOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={onCreateReferralCode}>
            Create Referral Code
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default ReferralCodeManagementTab;
